// FT-147 (REQ-02, FM-05): чистые помощники ветки «По счёту» — разбор строки
// платёжного QR ГОСТ Р 56042-2014 (формат ST00012) и клиентские валидаторы
// реквизитов плательщика. Без Vue и без сети — покрывается юнитом (CHK-02).
//
// Строку ST00012 собирает бэкенд (TbankOpenapi::QrPayload); фронт только
// рендерит её в QR и при необходимости показывает поля. Парсер здесь —
// контракт на форму строки: заголовок + пары key=value через «|».

export const ST00012_HEADER = "ST00012";

// Разбирает payload в { header, fields }. Значения режутся по ПЕРВОМУ «=»
// (ГОСТ допускает «=» внутри значения). Невалидная строка → null.
export function parseSt00012(payload) {
  if (typeof payload !== "string" || !payload.startsWith(ST00012_HEADER + "|")) return null;
  const [header, ...pairs] = payload.split("|");
  const fields = {};
  for (const pair of pairs) {
    const idx = pair.indexOf("=");
    if (idx <= 0) return null;
    fields[pair.slice(0, idx)] = pair.slice(idx + 1);
  }
  return { header, fields };
}

// Sum в ST00012 — копейки целым числом.
export function sumFromSt00012(payload) {
  const parsed = parseSt00012(payload);
  if (!parsed || !/^\d+$/.test(parsed.fields.Sum || "")) return null;
  return Number(parsed.fields.Sum) / 100;
}

// ИНН: 10 цифр — юрлицо, 12 — ИП.
export function isValidInn(inn) {
  return /^(\d{10}|\d{12})$/.test(String(inn || ""));
}

// ИП — 12-значный ИНН; КПП у ИП не бывает.
export function isSoleProprietor(inn) {
  return String(inn || "").length === 12;
}

// КПП: 9 цифр, обязателен для юрлица (ИНН 10) и не допускается для ИП (ИНН 12).
export function isValidKpp(inn, kpp) {
  const value = String(kpp || "");
  if (isSoleProprietor(inn)) return value === "";
  return /^\d{9}$/.test(value);
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || ""));
}

// Реквизиты контрагента по умолчанию: организация — из филиала, email — из
// текущего пользователя. КПП не подставляем ИП.
export function defaultRequisites(company, user) {
  const c = company || {};
  const inn = c.inn || "";
  return {
    name: c.company_name || c.name || "",
    inn,
    kpp: isSoleProprietor(inn) ? "" : c.kpp || "",
    email: (user && user.email) || "",
  };
}

// Реквизиты к отправке: у ИП КПП всегда null — даже если в форме осталось
// значение от прежнего 10-значного ИНН (поле у ИП заблокировано, стереть его
// пользователь не может).
export function requisitesPayload(r) {
  const req = r || {};
  return { ...req, kpp: isSoleProprietor(req.inn) ? null : req.kpp };
}

// Первая ошибка формы реквизитов как i18n-ключ (или null). Проверяется то,
// что реально уйдёт на бэк (нормализованный payload); те же правила — в
// TbankInvoice::Requisites.
export function requisitesErrorKey(r) {
  const req = requisitesPayload(r);
  if (!req.name) return "errors.required_field";
  if (!isValidInn(req.inn)) return "transfers.tbank_inn_invalid";
  if (!isValidKpp(req.inn, req.kpp)) return "transfers.tbank_kpp_invalid";
  if (!isValidEmail(req.email)) return "transfers.tbank_email_invalid";
  return null;
}


// Подписи локальных статусов счёта T-Bank (Transfer::TBANK_INVOICE_*_STATUS).
export const TBANK_INVOICE_STATUS_KEYS = {
  TBANK_INVOICE_CREATED: "transfers.tbank_status_created",
  TBANK_INVOICE_SUBMITTED: "transfers.tbank_status_submitted",
  TBANK_INVOICE_EXECUTED: "transfers.tbank_status_executed",
  TBANK_INVOICE_EXPIRED: "transfers.tbank_status_expired",
  TBANK_INVOICE_INIT_FAILED: "transfers.tbank_status_init_failed",
  TBANK_INVOICE_MANUAL: "transfers.tbank_status_manual",
  TBANK_INVOICE_CANCELLED: "transfers.tbank_status_cancelled",
};

// i18n-ключ подписи строки леджера для счёта T-Bank или null, если строка не счёт.
// Вызывается ДО общей ветки «успех» (state=1&&paid): у оплаченных счетов своя подпись
// (EXECUTED/MANUAL) — порядок защищён юнитом.
export function tbankLedgerStatusKey(item) {
  if (!item || item.payment_provider !== "tbank_invoice") return null;
  return TBANK_INVOICE_STATUS_KEYS[item.status] || "transfers.awaiting_payment";
}

// Цвет чипа для счёта T-Bank или null, если строка не счёт.
export function tbankLedgerChipColor(item) {
  if (!item || item.payment_provider !== "tbank_invoice") return null;
  if (item.paid) return "success";
  if (item.status === "TBANK_INVOICE_INIT_FAILED") return "error";
  if (item.status === "TBANK_INVOICE_CANCELLED") return "grey";
  return "warning";
}
